import styled from 'styled-components';
import { vars } from '../../theme';
import { semanticColors } from '../../styles/semanticColors';
import { applyOpacity } from '../../utils/color.utils';
import { readableColor } from 'polished';
import type { HBAvatarSize, HBStatusTheme } from './HBBrandAvatar.types';

export const LARGE_AVATAR = 40;
export const SMALL_AVATAR = 24;
export const STATUS_INDICATOR_SIZE = 6;
export const STATUS_INDICATOR_BORDER = 1;

const INDICATOR_COLORS: Record<HBStatusTheme, string> = {
    neutral: vars.colors.base.neutral,
    accent: vars.colors.base.accent,
    success: vars.colors.base.success,
    warning: vars.colors.base.warning,
    danger: vars.colors.base.danger
};

export const BrandAvatarWrapper = styled.div<{
    $size: HBAvatarSize;
    $clipPath?: string;
}>`
    position: relative;
    width: ${({ $size }) =>
        $size === 'large' ? `${LARGE_AVATAR}px` : `${SMALL_AVATAR}px`};
    height: ${({ $size }) =>
        $size === 'large' ? `${LARGE_AVATAR}px` : `${SMALL_AVATAR}px`};
    box-sizing: unset;
    border-radius: ${vars.radius.xs};

    // subtle inset border on top of the image, so the container is still visible with dark logos
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        box-shadow: inset 0 0 0 1px
            ${applyOpacity(vars.colors.surface.container, 20)};
        border-radius: inherit;
        pointer-events: none;

        ${({ $clipPath }) => $clipPath}
    }

    > * {
        border-radius: ${vars.radius.xs};
    }

    img {
        height: 100%;
        width: 100%;
    }
`;

export const InnerBrandAvatarWrapper = styled.div<{
    $clipPath?: string;
}>`
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-color: ${vars.colors.surface.item};

    ${({ $clipPath }) => $clipPath}
`;

export const InitialsLogo = styled.div<{
    $size: HBAvatarSize;
    $brandColor?: string;
}>`
    background-color: ${({ $brandColor }) =>
        $brandColor || vars.colors.base.neutral};
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;

    span {
        height: ${({ $size }) => ($size === 'large' ? '33px' : 'auto')};
        font-size: ${({ $size }) => ($size === 'large' ? '24px' : '15px')};
        line-height: ${({ $size }) => ($size === 'large' ? '1.45' : '1.4')};
        color: ${({ $brandColor }) =>
            readableColor(
                $brandColor ?? 'black',
                semanticColors.text.invertedPrimary,
                semanticColors.text.primary
            )};
        font-weight: 600;
        text-transform: uppercase;
    }
`;

export const StyledStatusIndicator = styled.div<{ status: HBStatusTheme }>`
    position: absolute;
    background-color: ${({ status }) => INDICATOR_COLORS[status]};
    border-radius: ${vars.radius.full};

    --indicator-size: ${STATUS_INDICATOR_SIZE}px;

    width: var(--indicator-size);
    height: var(--indicator-size);
    top: calc(var(--indicator-size) / -2);
    right: calc(var(--indicator-size) / -2);
`;
